import React, { useState } from 'react';
import { Bug, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';

/**
 * Per-widget dev-mode trace panel.
 *
 * Renders nothing without a trace. Shows the data source actually used (and
 * why), the formula with real numbers substituted, the raw inputs, and any
 * parse/fetch warnings. Only mounted when dev mode is on (see useDevMode).
 */
const MetricDebugInfo = ({ trace }) => {
  const [open, setOpen] = useState(false);
  if (!trace) return null;

  const hasWarnings = (trace.warnings || []).length > 0;

  return (
    <div className="mt-3 border-t border-dashed border-amber-300 dark:border-amber-700 pt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-300"
      >
        <Bug className="w-3 h-3" />
        dev: {trace.source}
        {hasWarnings && <AlertTriangle className="w-3 h-3 text-red-500" />}
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {open && (
        <div className="mt-2 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 p-3 text-[11px] leading-relaxed font-mono text-gray-800 dark:text-gray-200 space-y-2 overflow-x-auto">
          <div>
            <span className="font-semibold text-amber-800 dark:text-amber-300">source:</span> {trace.source}
            <div className="text-gray-600 dark:text-gray-400">{trace.sourceReason}</div>
          </div>

          {trace.formula && (
            <div>
              <span className="font-semibold text-amber-800 dark:text-amber-300">formula:</span>
              <div className="whitespace-pre-wrap break-words">{trace.formula}</div>
            </div>
          )}

          {trace.inputs && (
            <div>
              <span className="font-semibold text-amber-800 dark:text-amber-300">inputs:</span>
              <pre className="whitespace-pre-wrap break-words text-[10px] mt-0.5">{JSON.stringify(trace.inputs, null, 2)}</pre>
            </div>
          )}

          {hasWarnings && (
            <div>
              <span className="font-semibold text-red-600 dark:text-red-400">warnings:</span>
              <ul className="list-disc pl-4 text-red-600 dark:text-red-400">
                {trace.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="text-gray-400 dark:text-gray-500">computed {trace.computedAt}</div>
        </div>
      )}
    </div>
  );
};

export default MetricDebugInfo;
